import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom-v5-compat";
import { useLocation, useHistory } from "react-router-dom";
import qs from "query-string";

import {
  AuthContext,
  JobsContext,
  MatchContext,
  NotesContext,
  SearchContext,
} from "context/providers";
import { contextActions } from "context/actions";
import { TABS } from "context/constants/common";

import Applications from "components/Applications/index";
import JobDetailsPageHeader from "components/JobDetailsPageHeader";
import {
  COMMUNITY_BOOST_LEVELS,
  DEFAULT_RATE_RANGE,
  PLACEMENT_FILTERS,
  USER_LIST_VIEWS,
  USER_TYPES,
  YEARS_OF_EXPERIENCE_FILTER_STR_TEMPLATE,
} from "lookup";
import { convertSalaryToRatePerHour } from "utils/helpers/users";
import { getDefaultExcludeBHStatusFilterStr } from "utils/helpers/bullhorn";
import { getFilteredUserIDs } from "utils/helpers/search";
import {
  getUserListPreferences,
  normalizeUserListViewType,
} from "utils/helpers/userListPreferences";
import {
  getBullhornPublishedRates,
  isBullhornPublishedSalary,
} from "utils/common";

const getMaxRate = (jobOpp) => {
  if (jobOpp.maxTalentRate?.value != null) {
    return jobOpp.maxTalentRate.value;
  }

  if (jobOpp.maxTalentSalary?.value != null) {
    return convertSalaryToRatePerHour(jobOpp.maxTalentSalary.value);
  }

  const bullhornRates = getBullhornPublishedRates(jobOpp?.metadata);

  if (bullhornRates.high?.value != null) {
    return isBullhornPublishedSalary(bullhornRates.high)
      ? convertSalaryToRatePerHour(bullhornRates.high.value)
      : bullhornRates.high.value;
  }

  if (jobOpp.maxRate?.value != null) {
    return jobOpp.maxRate.value;
  }

  if (jobOpp.maxSalary?.value != null) {
    return convertSalaryToRatePerHour(jobOpp.maxSalary.value);
  }

  return DEFAULT_RATE_RANGE.max;
};

const JobOppDetails = ({ user }) => {
  const params = useParams();
  const history = useHistory();
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const searchQuery = queryParams.get("search");

  const {
    setSearchState,
    clearSearchState,
    geographicalRegions,
    setGeographicalRegions,
    handleLoadAISkills,
    handleReRankHitsForJob,
    handleReadyForSearch,
    handlePageIsLoaded,
    setHits,
    pageIsLoaded,
    hideSkipped,
    hideRejected,
    hideMatchedAndApplied,
    searchState,
    setSkillSearchState,
  } = useContext(SearchContext);

  const { user: loggedInUser } = useContext(AuthContext);

  const { loadingJobOpp, jobOpp, initJob, initJobCalendarEvents, clearJob } =
    useContext(JobsContext);

  const { jobOppNotes, job: initJobNotes } = useContext(NotesContext);

  const { clearMatches } = useContext(MatchContext);

  const initialMountRef = useRef(false);

  const hasFetchAISkills = useRef(null);

  const jobOppIdIsParamId = jobOpp.id && params.id && jobOpp.id === params.id;

  const initNotes = async () => {
    try {
      await initJobNotes(jobOpp);
    } catch (error) {
      console.error("initJobNotes error: ", error);
    }
  };

  const [error, setError] = useState(false);

  const initJobOpp = async () => {
    try {
      await initJob(params.id);
    } catch (error) {
      console.error("initJobOpportunity error: ", error);
    }
  };

  const updateTabQs = (uniqueId) => {
    const queryString = qs.stringify({
      ...qs.parse(location.search),
      tab: uniqueId,
    });

    navigate(`${location.pathname}?${queryString}`, { replace: true });
  };

  useEffect(() => {
    (async () => {
      initialMountRef.current = false;
      await initJobOpp();
    })();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  useEffect(() => {
    if (!jobOpp && !loadingJobOpp) setError(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobOpp]);

  const maxRate = getMaxRate(jobOpp);

  const prepareInitialJobState = async () => {
    const definedRate = {
      min: 0,
      max: Math.min(maxRate, DEFAULT_RATE_RANGE.max),
    };

    const newSearchState = await handleLoadAISkills({
      paramsId: params.id,
      canFetch: isSearchResultsTab,
      noCache: false,
    });

    const finalState = {
      ...(newSearchState ? newSearchState : {}),
      range: {
        "ratePerHour.value": definedRate,
      },
      refinementList: {
        userType: [USER_TYPES.FREELANCER],
        availability: [jobOpp.timeCommitment, "OPENTOOFFERS"],
      },
      locationFilterStr: "",
      locationFilter: {},
      placementFilterStr: PLACEMENT_FILTERS.notHasActivePlacements,
      BHStatusFilterStr: getDefaultExcludeBHStatusFilterStr(),
    };

    if (jobOpp?.requiredExperience) {
      finalState.yearsOfExperienceFilterStr =
        YEARS_OF_EXPERIENCE_FILTER_STR_TEMPLATE.replace(
          "{!value}",
          jobOpp?.requiredExperience
        );
    }

    // If the job has geographical regions defined, use it
    if (jobOpp.geographicalRegions?.length > 0) {
      // Create a new array based on geographicalRegions with the added 'checked' attribute
      const updatedGeographicalRegions = geographicalRegions.map((region) => {
        const existsInJobOpp = jobOpp.geographicalRegions.some(
          (jobRegion) =>
            jobRegion.regionName === region.regionName &&
            jobRegion.countryNames.length === 0
        );
        return { ...region, checked: existsInJobOpp };
      });

      // Consider only those countries that are mentioned, not all countries in the region
      // Unless, only region is specified, in which case consider all countries in the region
      const selectedCountries = jobOpp.geographicalRegions
        .map((g) => {
          if (g.countryNames.length === 0) {
            const selectedRegion = geographicalRegions.find(
              (gr) => gr.regionName === g.regionName
            );

            if (selectedRegion) {
              return selectedRegion.countryNames;
            }
          }
          return g.countryNames;
        })
        .flat();

      setGeographicalRegions(updatedGeographicalRegions, false);

      finalState.refinementList = {
        ...finalState.refinementList,
        "location.countryName": selectedCountries,
      };
    }

    if (jobOpp.location) {
      finalState.locationFilter = {
        countryName: jobOpp.location.countryName,
        stateName: jobOpp.location.stateName,
        cityName: jobOpp.location.cityName,
        aroundLatLng:
          jobOpp.location.latitude && jobOpp.location.longitude
            ? `${jobOpp.location.latitude}, ${jobOpp.location.longitude}`
            : undefined,
        aroundRadius: 50,
      };

      finalState.locationFilterStr = contextActions.search.getLocationFilterStr(
        finalState.locationFilter
      );

      if (finalState.refinementList?.["location.countryName"]) {
        if (
          !finalState.refinementList["location.countryName"].includes(
            jobOpp.location.countryName
          )
        ) {
          finalState.refinementList?.["location.countryName"].push(
            jobOpp.location.countryName
          );
        }
      } else {
        finalState.refinementList = {
          ...finalState.refinementList,
          "location.countryName": [jobOpp.location.countryName],
        };
      }
    }

    finalState.configProps = {
      ...finalState.configProps,
      facetsExcludes: {
        objectID: getFilteredUserIDs(
          jobOpp.matches?.items,
          hideSkipped,
          hideRejected,
          hideMatchedAndApplied
        ),
      },
    };

    finalState.communityBoost = loggedInUser?.canAccessCommunityBoost
      ? COMMUNITY_BOOST_LEVELS.MEDIUM
      : COMMUNITY_BOOST_LEVELS.NONE;

    setSearchState(finalState);
  };

  const cleanUpSearchParams = () => {
    // Deleting query params to not persist state in the URL
    queryParams.delete("search");
    const newUrl =
      location.pathname +
      (queryParams.size > 0 ? `?${queryParams.toString()}` : "");
    history.replace(newUrl);
  };

  useEffect(() => {
    hasFetchAISkills.current = isSearchResultsTab || null;

    return () => {
      handlePageIsLoaded(false);
      handleReadyForSearch(false);
      setHits([]);
      clearJob();
      clearMatches();
      clearSearchState({ isUnMounting: true });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    (async () => {
      if (!loadingJobOpp && jobOppIdIsParamId) {
        document.title = `Torc Search Tool - ${jobOpp.title}`;

        await initJobCalendarEvents(params.id);

        await prepareInitialJobState();

        if (!jobOppNotes[jobOpp.id]) {
          initNotes();
        }
        handleReadyForSearch(true);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadingJobOpp]);

  const userListPreferences = getUserListPreferences(loggedInUser);
  const userListPreferencesAreReady = !!(
    loggedInUser?.id ||
    loggedInUser?.username ||
    loggedInUser?.email ||
    loggedInUser?.attributes?.sub ||
    loggedInUser?.attributes?.email
  );
  const currentViewType = normalizeUserListViewType(
    queryParams.get("viewType") || userListPreferences.viewType
  );
  const tab = queryParams.get("tab");

  const isSearchResultsTab = !tab || tab === TABS.SEARCHRESULTS;

  const isCareerTrackAnalysisEnabledAndCanBeAccessed =
    userListPreferences.careerTrackAnalysisEnabled &&
    loggedInUser.canAccessMatchAgent;

  const isMatchAgentViewAndCanBeAccessed =
    isCareerTrackAnalysisEnabledAndCanBeAccessed &&
    currentViewType === USER_LIST_VIEWS.MATCH_AGENT;

  const handleSearchQueryChange = () => {
    cleanUpSearchParams();

    /* FOR GOING BACK TO ADVANCED */
    if (isMatchAgentViewAndCanBeAccessed && isSearchResultsTab) {
      handleReRankHitsForJob({
        id: "",
        previousValue: {
          id: jobOpp.id || "",
        },
      });
    } else {
      handleReRankHitsForJob("", "id");
    }

    const previousAdvanceSearchStateStr = !searchState.basicSearch
      ? JSON.stringify(searchState)
      : searchState?.previousAdvanceSearchStateStr || "{}";

    /* FOR GOING BACK TO ADVANCED */

    clearSearchState({
      previousAdvanceSearchStateStr,
      isUnMounting: true,
      urlParamData: {
        customSearchQuery: searchQuery,
        basicSearch: true,
      },
    });
  };

  // Initial state is set and widgets have triggered
  // handle pageIsLoaded
  useEffect(() => {
    if (tab === TABS.PRECANDIDATES && !loggedInUser.canAccessPreCandidates) {
      updateTabQs(TABS.SEARCHRESULTS);

      return;
    }

    if (
      pageIsLoaded &&
      userListPreferencesAreReady &&
      !initialMountRef.current &&
      jobOppIdIsParamId
    ) {
      initialMountRef.current = true;

      if (!searchQuery) {
        if (
          isCareerTrackAnalysisEnabledAndCanBeAccessed &&
          isSearchResultsTab
        ) {
          handleReRankHitsForJob({
            id: jobOpp.id || "",
            isLoading: true,
          });
        } else {
          handleReRankHitsForJob("", "id");
        }
      } else if (searchQuery) {
        handleSearchQueryChange();
      }

      setTimeout(() => {
        handleReadyForSearch(true);
      }, 350);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIsLoaded, jobOpp.id, userListPreferencesAreReady]);

  useEffect(() => {
    if (
      pageIsLoaded &&
      searchQuery &&
      initialMountRef.current &&
      jobOppIdIsParamId
    ) {
      handleSearchQueryChange();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  useEffect(() => {
    if (
      isSearchResultsTab &&
      pageIsLoaded &&
      hasFetchAISkills.current === null
    ) {
      (async () => {
        hasFetchAISkills.current = false;
        const result = await handleLoadAISkills({
          paramsId: params.id,
          noCache: false,
        });
        hasFetchAISkills.current = true;

        if (!result) return;
        setSkillSearchState({ ...result.skills });
      })();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSearchResultsTab]);

  if (loadingJobOpp) {
    return (
      <div className="flex justify-center pt-8">
        <span className="loader"></span>
      </div>
    );
  }

  return error || !jobOpp ? (
    <div className="flex justify-center pt-8">
      <p className="text-red-400 text-xl">
        The job opportunity could not be fetched successfully. Please try again.
        If problem persists, contact administrator about this issue.
      </p>
    </div>
  ) : (
    <div data-testid="job-details-page">
      <JobDetailsPageHeader jobOpp={jobOpp} user={user} />
      <Applications user={user} />
    </div>
  );
};

export default JobOppDetails;
